import type { HttpHeaders, HttpMethod, PathParams, PathQuery } from './types'
import type { APIRequest, APISuccess } from './APIClient'

/** Reusable argument list for methods that execute API calls */
export type APICallArgs<
  TPaths extends string,
  TBody = unknown,
  TOptions extends APICallOptions = APICallOptions,
> = [method: HttpMethod, apiPath: APIFullPath<TPaths>, body?: TBody | null, options?: TOptions]

/** Path-related API call properties */
export type APIFullPath<TPaths extends string> = {
  path: TPaths
  params?: PathParams
  query?: PathQuery
}

/** Additional API call options */
export type APICallOptions = {
  customHeaders?: HttpHeaders
  fullResponse?: boolean
  credentials?: RequestCredentials
}

/** API call options that result in data being returned */
export type APIDataOnlyCallArgs = APICallOptions & { fullResponse?: false }

/** API call options that result in the whole response being returned */
export type APIFullResponseCallArgs = APICallOptions & { fullResponse: true }

/** The API call result, where the shape depends on passed options */
export type APICallResult<
  TData,
  TResponseFields extends object,
  TOptions extends APICallOptions,
> = TOptions extends APIFullResponseCallArgs ? APISuccess<TData, TResponseFields> : TData

/**
 * Reusable helper to resolve API calls
 *
 * - turns API call arguments into a request
 * - sends it using the provided handler
 * - returns the appropriate result shape depending on options
 */
export const resolveAPICall = async <
  TData,
  TPaths extends string,
  TResponseFields extends object,
  TOptions extends APICallOptions,
>(
  args: APICallArgs<TPaths, unknown, TOptions>,
  handler: (request: APIRequest) => Promise<APISuccess<TData, TResponseFields>>,
): Promise<APICallResult<TData, TResponseFields, TOptions>> => {
  const request = buildAPIRequest(...args)
  const response = await handler(request)

  return (args[3]?.fullResponse ? response : response.data) as APICallResult<
    TData,
    TResponseFields,
    TOptions
  >
}

const buildAPIRequest = <TPaths extends string, TOptions extends APICallOptions>(
  method: HttpMethod,
  { path, params = {}, query = {} }: APIFullPath<TPaths>,
  body: unknown,
  options?: TOptions,
): APIRequest => {
  return {
    method,
    path,
    pathParams: params,
    query,
    body,
    headers: { ...options?.customHeaders },
    credentials: options?.credentials,
  }
}
